import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, MapPin, Phone } from 'lucide-react';
import { COMPANY, NAV_LINKS, ROUTES } from '@/lib/data';
import { trackEvent } from '@/lib/analytics';

const TikTokIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
        <path d="M14.5 3h2.6c.2 1.7 1.1 3.1 2.6 4 .9.5 1.9.8 3 .8v2.7c-1.9 0-3.7-.6-5.2-1.7v6.8c0 3.6-2.9 6.6-6.6 6.6S4.3 19.2 4.3 15.6c0-3.6 2.9-6.6 6.6-6.6.4 0 .8 0 1.2.1V12c-.4-.1-.8-.2-1.2-.2-2.1 0-3.8 1.7-3.8 3.8s1.7 3.8 3.8 3.8 3.8-1.7 3.8-3.8V3z"/>
    </svg>
);

export const Footer = () => {
    const emprenovusLogo = COMPANY.emprenovusLogo;
    const mapsUrl = 'https://maps.app.goo.gl/pqDoBEqysbpW8CzFA';
    const mapsEmbedUrl =
    'https://www.google.com/maps?q=Kche%20Diversiones%20%40-0.2300743,-78.325875&z=17&output=embed';
    const onSocial = (p) => trackEvent('social_click', window.location.pathname, { platform: p, from: 'footer' });
    const onMap = () => trackEvent('map_click', window.location.pathname, { from: 'footer' });

    return (
        <footer data-testid="site-footer" className="bg-[hsl(var(--kche-dark))] text-on-dark relative overflow-hidden">
            <div className="absolute -top-24 -left-16 w-96 h-96 rounded-full bg-[hsl(var(--kche-pink))]/15 blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-24 -right-16 w-96 h-96 rounded-full bg-[hsl(var(--kche-cyan))]/15 blur-3xl" aria-hidden="true" />

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-14 pb-8 grid gap-10 md:grid-cols-4">
                <div className="md:col-span-1">
                    <Link to={ROUTES.home} aria-label={COMPANY.name}>
                        <img src={COMPANY.logo} alt={COMPANY.name} className="h-32 w-32 sm:h-36 sm:w-36 object-contain" />
                    </Link>
                    <p className="mt-4 text-sm text-on-dark/80 max-w-xs">
                        Parque de diversiones familiar en Pifo. Megajuegos, Zona Arcade y experiencias para toda la familia.
                    </p>
                </div>

                <div>
                    <h4 className="font-display text-lg text-on-dark mb-3">Navegación</h4>
                    <ul className="space-y-2 text-sm text-on-dark/85">
                        {NAV_LINKS.map((l) => (
                            <li key={l.to}>
                                <Link to={l.to} className="hover:text-[hsl(var(--kche-yellow))] transition-colors">
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h4 className="font-display text-lg text-on-dark mb-3">Contacto</h4>
                    <ul className="space-y-2 text-sm text-on-dark/90">
                        <li className="flex items-start gap-2">
                            <Phone className="size-4 mt-0.5 text-[hsl(var(--kche-yellow))]" />
                            <span>{COMPANY.phone}</span>
                        </li>
                        {COMPANY.phoneSecondary ? (
                            <li className="flex items-start gap-2">
                                <Phone className="size-4 mt-0.5 text-[hsl(var(--kche-yellow))]" />
                                <span>{COMPANY.phoneSecondary}</span>
                            </li>
                        ) : null}
                        <li className="flex items-start gap-2">
                            <MapPin className="size-4 mt-0.5 text-[hsl(var(--kche-pink))]" />
                            <a
                                href={mapsUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={onMap}
                                className="hover:text-[hsl(var(--kche-cyan))]"
                                >
                                {COMPANY.address}
                            </a>
                        </li>
                        {COMPANY.email ? (
                            <li className="flex items-start gap-2">
                                <Mail className="size-4 mt-0.5 text-[hsl(var(--kche-green))]" />
                                <a href={`mailto:${COMPANY.email}`} className="hover:text-[hsl(var(--kche-cyan))]">
                                    {COMPANY.email}
                                </a>
                            </li>
                        ) : (
                            <li className="text-on-dark/55 text-xs">Email: pendiente de configuración</li>
                        )}
                    </ul>
                    <div className="mt-4 flex items-center gap-3">
                        <a
                            href={COMPANY.instagramUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-instagram-link"
                            onClick={() => onSocial('instagram')}
                            aria-label="Instagram"
                            className="size-10 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-[hsl(var(--kche-pink))] hover:text-white transition"
                        >
                            <Instagram className="size-4" />
                        </a>
                        <a
                            href={COMPANY.tiktokUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-tiktok-link"
                            onClick={() => onSocial('tiktok')}
                            aria-label="TikTok"
                            className="size-10 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-[hsl(var(--kche-cyan))] hover:text-[hsl(var(--kche-dark))] transition"
                        >
                            <TikTokIcon className="size-4" />
                        </a>
                        <a
                            href={COMPANY.facebookUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-facebook-link"
                            onClick={() => onSocial('facebook')}
                            aria-label="Facebook"
                            className="size-10 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-[hsl(var(--kche-blue))] hover:text-white transition"
                        >
                            <Facebook className="size-4" />
                        </a>
                    </div>
                </div>

                <div>
                    <h4 className="font-display text-lg text-on-dark mb-3">Legal</h4>
                    <ul className="space-y-2 text-sm text-on-dark/85">
                        <li><Link to={ROUTES.privacidad} className="hover:text-[hsl(var(--kche-yellow))]">Política de privacidad</Link></li>
                        <li><Link to={ROUTES.normativas} className="hover:text-[hsl(var(--kche-yellow))]">Normativas del parque</Link></li>
                    </ul>
                    <p className="text-xs text-on-dark/55 mt-3">Algunos textos legales están pendientes de confirmación por el cliente.</p>
                </div>
            </div>

            {/* Full-width Google Maps iframe, just above Emprenovus branding */}
            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-6">
                <div className="rounded-3xl overflow-hidden border border-white/10 shadow-pop">
                    <iframe
                        title="Mapa K'che Diversiones Pifo"
                        src={mapsEmbedUrl}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                        className="w-full h-[280px] sm:h-[360px] border-0"
                        data-testid="footer-map-iframe"
                    />
                </div>
            </div>

            {/* Mandatory Emprenovus block */}
            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="mt-2 pt-6 border-t border-primary-foreground/15">
                    <div
                        data-testid="footer-emprenovus-block"
                        className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-xs text-primary-foreground/75 text-center"
                    >
                        <span>
                            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
                        </span>

                        <span className="hidden sm:inline opacity-40">|</span>

                        <a
                            href="https://www.emprenovus.com"
                            target="_blank"
                            rel="noreferrer"
                            className="group inline-flex items-center gap-2 text-on-dark/80 hover:text-[hsl(var(--kche-yellow))] transition-colors duration-300"
                        >
                            <img
                                src={emprenovusLogo}
                                className="h-5 w-5 object-contain opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                                alt="Emprenovus"
                            />
                            <span>
                                AI powered by <span className="font-medium">Emprenovus</span>
                            </span>
                        </a>
                    </div>
                </div>
            </div>
            <div className="h-6" />
        </footer>
    );
};

export default Footer;
